// src/utils/api.js

// ✅ Use consistent API_BASE
const API_BASE = (process.env.REACT_APP_API_URL || 'https://localhost:7018/api').trim().replace(/\/$/, '');

// ✅ Use consistent storage key
const TOKEN_STORAGE_KEY = 'fsl_auth_credentials';

/**
 * Generate new auth credentials from backend
 */
export async function generateToken() {
  try {
    const response = await fetch(`${API_BASE}/auth/generate-token`, {
      method: 'POST',
      headers: {
        'AuthorizationKey': 'FSLAuthorizedKeys123',
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({}),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error');
      console.error('❌ Token generation failed:', errorText);
      throw new Error(`Failed to generate token: ${errorText}`);
    }

    const data = await response.json();
    
    return {
      authorizationKey: data.authorizationKey || data.AuthorizationKey || '',
      token: data.token || data.Token || '',
      secretKey: data.secretKey || data.SecretKey || '',
      expiresAt: data.expiresAt ? new Date(data.expiresAt).getTime() : null,
    };
  } catch (error) {
    console.error('❌ generateToken error:', error);
    throw error;
  }
}

/**
 * Get stored credentials if valid
 */
export function getStoredCredentials() {
  try {
    const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!stored) return null;
    
    const credentials = JSON.parse(stored);
    
    if (!credentials.authorizationKey || !credentials.token || !credentials.secretKey) {
      console.warn('⚠️ Stored credentials incomplete');
      return null;
    }
    
    if (credentials.expiresAt && Date.now() > credentials.expiresAt) {
      console.warn('⚠️ Credentials expired');
      clearStoredCredentials();
      return null;
    }
    
    return credentials;
  } catch (error) {
    console.error('❌ Failed to parse stored credentials:', error);
    return null;
  }
}

/**
 * Store credentials securely
 */
export function storeCredentials(credentials) {
  try {
    localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(credentials));
  } catch (error) {
    console.error('❌ Failed to store credentials:', error);
  }
}

/**
 * Clear stored credentials
 */
export function clearStoredCredentials() {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
}

/**
 * Get auth headers for manual fetch calls
 */
export function getAuthHeaders() {
  const credentials = getStoredCredentials();
  if (!credentials) return null;
  
  return {
    'AuthorizationKey': credentials.authorizationKey,
    'Token': credentials.token,
    'SecretKey': credentials.secretKey,
  };
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated() {
  return !!getStoredCredentials();
}

/**
 * ✅ FIXED: Authenticated fetch utility
 * Now accepts options object as 4th parameter for headers and signal
 */
export async function authenticatedFetch(url, method = 'GET', body = null, options = {}) {
  // Try to get existing valid credentials first
  let credentials = getStoredCredentials();
  
  // If no valid credentials, generate new ones
  if (!credentials) {
    try {
      credentials = await generateToken();
      storeCredentials(credentials);
    } catch (error) {
      console.error('❌ Failed to generate credentials:', error);
      throw new Error('Authentication failed. Please log in again.');
    }
  }

  const { token, secretKey, authorizationKey } = credentials;
  const isFormData = body instanceof FormData;
  
  // ✅ Build base headers with stored credentials
  const headers = {
    'AuthorizationKey': authorizationKey,
    'Token': token,
    'SecretKey': secretKey,
  };

  // ✅ Only set Content-Type for JSON requests
  if (!isFormData) {
    headers['Content-Type'] = 'application/json';
    if (body && typeof body === 'object') {
      body = JSON.stringify(body);
    }
  }

  // ✅ Merge custom headers from options (for Settings endpoints with env vars)
  if (options.headers) {
    Object.keys(options.headers).forEach(key => {
      const value = options.headers[key];
      // Only override if value is not empty
      if (value !== null && value !== undefined && value !== '') {
        headers[key] = value;
      }
    });
  }

  // ✅ Extract signal from options
  const signal = options.signal || null;

  try {
    const response = await fetch(url, { 
      method, 
      headers, 
      body: isFormData ? body : body,
      signal
    });

    // Handle 401/403 - retry once with fresh token
    if (response.status === 401 || response.status === 403) {
      console.warn('⚠️ Auth failed, attempting token refresh...');
      clearStoredCredentials();
      
      try {
        const newCredentials = await generateToken();
        storeCredentials(newCredentials);
        
        const retryHeaders = {
          'AuthorizationKey': newCredentials.authorizationKey,
          'Token': newCredentials.token,
          'SecretKey': newCredentials.secretKey,
        };
        
        if (!isFormData) {
          retryHeaders['Content-Type'] = 'application/json';
        }
        
        // Merge custom headers for retry
        if (options.headers) {
          Object.keys(options.headers).forEach(key => {
            const value = options.headers[key];
            if (value !== null && value !== undefined && value !== '') {
              retryHeaders[key] = value;
            }
          });
        }
        
        const retryResponse = await fetch(url, { 
          method, 
          headers: retryHeaders, 
          body: isFormData ? body : body,
          signal
        });
        
        if (!retryResponse.ok) {
          const errorData = await retryResponse.json().catch(() => ({}));
          const msg = errorData.message?.toLowerCase() || '';
          
          if (msg.includes('invalid token') || msg.includes('invalid secret') || msg.includes('authorization')) {
            clearStoredCredentials();
            throw new Error('Session expired. Please log in again.');
          }
          
          throw new Error(errorData.message || `HTTP ${retryResponse.status}`);
        }
        
        return await retryResponse.json();
      } catch (refreshError) {
        console.error('❌ Token refresh failed:', refreshError);
        clearStoredCredentials();
        throw refreshError;
      }
    }

    // Parse JSON safely
    let responseData = null;
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      try {
        responseData = await response.json();
      } catch (err) {
        console.warn('⚠️ Failed to parse JSON response:', err);
      }
    }

    if (!response.ok) {
      const message = responseData?.message || `HTTP ${response.status}`;
      const msg = message.toLowerCase();
      
      if (msg.includes('invalid token') || msg.includes('invalid secret') || msg.includes('authorization')) {
        clearStoredCredentials();
      }
      
      throw new Error(message);
    }

    return responseData;
  } catch (error) {
    console.error('❌ authenticatedFetch error:', error);
    
    const errMsg = error.message?.toLowerCase() || '';
    if (errMsg.includes('token') || 
        errMsg.includes('secretkey') ||
        errMsg.includes('authorizationkey') ||
        errMsg.includes('failed to fetch') ||
        errMsg.includes('networkerror')) {
      clearStoredCredentials();
    }
    
    throw error;
  }
}